import React from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import type {
  CampaignFeasibilityResponse,
  GeoKind,
} from '../../services/feasibilityService';
import {
  formatExtensionDateCopy,
  formatTotalRatioCopy,
  GEO_COVERAGE_LOW_THRESHOLD_PCT,
  stateNameForCode,
} from './shared/feasibilityCopy';
import Banner from './shared/Banner';
import { DMA_REGIONS } from '../../data/dmaList';

// CTV-474: DMA code → friendly name. Used so the recommendation copy says
// "Honolulu (DMA 744)" instead of just "744". Built once at module load.
const DMA_NAME_BY_CODE: Map<string, string> = new Map(
  DMA_REGIONS.map((r) => [r.code, r.name]),
);

function dmaNameForCode(code: string): string {
  return DMA_NAME_BY_CODE.get(code) ?? code;
}

/**
 * CTV-474 — InventoryWarningCard (locked-in spec in Linear).
 *
 * - The card is hidden while loading; on any input change the parent hook
 *   clears the verdict so the warning animates *out* before the request
 *   fires. It re-mounts only when a new infeasible verdict arrives.
 * - "geography" replaces "geo" throughout user-facing copy.
 * - When `targetingMode === 'custom'` and `geoCoverage.percent < 75`, a
 *   geography block surfaces with tailored copy + action buttons.
 *   - radius: `[Center on {label}]` (when the station has center coords)
 *   - states: `[Add {label}]` for each recommended state
 *   - dmas:   `[Add {label}]` for each recommended DMA
 *   - zips:   `[Add suggested ZIPs]` appends all three
 */
interface InventoryWarningCardProps {
  verdict: CampaignFeasibilityResponse | null;
  accentColor: string;
  stationName?: string;
  /** Hide the geography block entirely (Extend panel — buyer can't change geo there). */
  suppressGeoBlock?: boolean;
  /**
   * Called with the suggested end date when the buyer clicks `Use {date}`.
   * Omit to hide the button (e.g. when the parent doesn't own date state).
   */
  onApplyExtensionDate?: (date: string) => void;
  /**
   * Called when the radius "Center on X" button is clicked. Argument is the
   * `(lat, lon, label)` the parent should apply to its geo state.
   */
  onApplyRadiusCenter?: (lat: number, lon: number, label: string) => void;
  /**
   * Called when an "Add X" geography button is clicked. The parent appends
   * the code to its corresponding selection list (`selectedStates`,
   * `selectedDMAs`). CTV-502: the `'zips'` kind is no longer reachable in
   * the live wizard because geoTargetZips only flows through radius mode,
   * which `effectiveKind` re-labels to `'radius'`.
   */
  onAddGeoCodes?: (kind: GeoKind, codes: string[]) => void;
  /**
   * Called when the radius block surfaces a "Run station-wide" button —
   * i.e. when the buyer has exhausted radius levers and the only remaining
   * action is to drop geography targeting entirely. The parent should flip
   * `targetingMode` to `'default'`.
   */
  onRemoveGeoTargeting?: () => void;
  /** Wizard-radius mode signal. Lets the card render the radius variant. */
  isRadiusMode?: boolean;
  /** Wizard radius miles. Used to suppress "expand up to 100 mi" copy at max. */
  radiusMiles?: number;
  /**
   * Current radius pin from the form. Used to suppress the "Center on X"
   * affordance when the buyer is already on the station's home market.
   * Property names match the form's geoTargetCenter shape (lng, not lon).
   */
  currentRadiusCenter?: { lat: number; lng: number; label?: string } | null;
}

// CTV-474: ~0.01° ≈ 1 km. Coarse enough that the recenter copy clears even
// when the buyer placed their pin a block away from the seeded city center.
const RADIUS_CENTER_TOLERANCE_DEG = 0.01;

const RADIUS_MAX_MILES = 100;

const TRANSITION_CLASS = 'transition-[grid-template-rows] duration-300 ease-out';

const InventoryWarningCard: React.FC<InventoryWarningCardProps> = ({
  verdict,
  accentColor,
  stationName,
  suppressGeoBlock = false,
  onApplyExtensionDate,
  onApplyRadiusCenter,
  onAddGeoCodes,
  onRemoveGeoTargeting,
  isRadiusMode = false,
  radiusMiles,
  currentRadiusCenter,
}) => {
  void accentColor;
  const showWarning = Boolean(verdict && !verdict.feasible);

  return (
    <div
      data-inventory-warning-card
      className={`grid w-full ${TRANSITION_CLASS}`}
      style={{ gridTemplateRows: showWarning ? '1fr' : '0fr' }}
      aria-hidden={!showWarning}
    >
      <div className="overflow-hidden">
        {verdict && (
          <WarningBody
            verdict={verdict}
            stationNameOverride={stationName}
            suppressGeoBlock={suppressGeoBlock}
            onApplyExtensionDate={onApplyExtensionDate}
            onApplyRadiusCenter={onApplyRadiusCenter}
            onAddGeoCodes={onAddGeoCodes}
            onRemoveGeoTargeting={onRemoveGeoTargeting}
            isRadiusMode={isRadiusMode}
            radiusMiles={radiusMiles}
            currentRadiusCenter={currentRadiusCenter}
          />
        )}
      </div>
    </div>
  );
};

// ---------- Body ----------

const WarningBody: React.FC<{
  verdict: CampaignFeasibilityResponse;
  stationNameOverride?: string;
  suppressGeoBlock: boolean;
  onApplyExtensionDate?: (date: string) => void;
  onApplyRadiusCenter?: (lat: number, lon: number, label: string) => void;
  onAddGeoCodes?: (kind: GeoKind, codes: string[]) => void;
  onRemoveGeoTargeting?: () => void;
  isRadiusMode: boolean;
  radiusMiles?: number;
  currentRadiusCenter?: { lat: number; lng: number; label?: string } | null;
}> = ({
  verdict,
  stationNameOverride,
  suppressGeoBlock,
  onApplyExtensionDate,
  onApplyRadiusCenter,
  onAddGeoCodes,
  onRemoveGeoTargeting,
  isRadiusMode,
  radiusMiles,
  currentRadiusCenter,
}) => {
  const stationName = stationNameOverride ?? verdict.stationName;
  const totalCopy = formatTotalRatioCopy(verdict, stationName);
  const extensionDateCopy = formatExtensionDateCopy(verdict.extensionDate);

  // CTV-474: backend only populates `geoCoverage` when targetingMode is
  // 'custom' AND a geography list is non-empty. When it's null the buyer is
  // on "Run on station" — no geography block, no "selected geography" copy.
  // The `isRadiusMode` signal only re-labels the wire-format 'zips' kind to
  // 'radius'; it never promotes a non-custom session into a custom one.
  const apiKind = verdict.geoCoverage?.kind ?? null;
  const effectiveKind: GeoKind | null =
    apiKind == null ? null : isRadiusMode ? 'radius' : apiKind;
  const coveragePct = verdict.geoCoverage?.percent ?? null;
  const showGeoBlock =
    !suppressGeoBlock &&
    effectiveKind != null &&
    coveragePct != null &&
    coveragePct < GEO_COVERAGE_LOW_THRESHOLD_PCT;

  const showApplyDateButton =
    onApplyExtensionDate != null &&
    typeof verdict.extensionDate === 'string' &&
    verdict.extensionDate.length > 0;

  const dateLine = computeDateLine({
    stationName,
    extensionDateCopy,
    hasCustomGeo: effectiveKind != null,
    coveragePct,
    suppressGeoBlock,
  });

  return (
    <Banner
      tone="orange"
      icon={<AlertTriangle className="w-3.5 h-3.5" />}
      label="Inventory is limited"
    >
      <p className="text-orange-800">{totalCopy}</p>
      <p className="mt-2 text-orange-800">{dateLine}</p>
      {showApplyDateButton && (
        <div className="mt-2.5">
          <button
            type="button"
            onClick={() => onApplyExtensionDate!(verdict.extensionDate!)}
            className="inline-flex items-center gap-1.5 rounded-full bg-orange-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 active:scale-[0.98]"
          >
            Use {extensionDateCopy}
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
      {showGeoBlock && (
        <GeoBlock
          kind={effectiveKind!}
          coveragePct={coveragePct!}
          stationName={stationName}
          verdict={verdict}
          radiusMiles={radiusMiles}
          currentRadiusCenter={currentRadiusCenter}
          onApplyRadiusCenter={onApplyRadiusCenter}
          onAddGeoCodes={onAddGeoCodes}
          onRemoveGeoTargeting={onRemoveGeoTargeting}
        />
      )}
    </Banner>
  );
};

// ---------- Helpers ----------

function computeDateLine(args: {
  stationName: string;
  extensionDateCopy: string | null;
  hasCustomGeo: boolean;
  coveragePct: number | null;
  suppressGeoBlock: boolean;
}): string {
  const { stationName, extensionDateCopy, hasCustomGeo, coveragePct, suppressGeoBlock } = args;
  if (extensionDateCopy) return `Extend to ${extensionDateCopy} for full delivery.`;
  // Extend panel: dedicated copy regardless of geo state.
  if (suppressGeoBlock) {
    return 'Try pushing back your end date further or reducing your additional budget to fit inventory constraints.';
  }
  // Default targeting OR custom geo with coverage already at/above the LOW
  // threshold: geography isn't the path forward.
  const geoFine =
    !hasCustomGeo ||
    (coveragePct != null && coveragePct >= GEO_COVERAGE_LOW_THRESHOLD_PCT);
  if (geoFine) {
    return `Even extending out 12 months, ${stationName} doesn't have enough open inventory for this campaign. Try pushing back your end date further or reducing your budget to fit inventory constraints.`;
  }
  // Custom geo + low coverage: point to the geography block below.
  return 'Even extending out 12 months, this campaign doesn\'t fit your selected geography. Try pushing back your end date further or expanding your geography (details below).';
}

// ---------- Geo block ----------

const GeoButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-orange-300 px-3 py-1.5 text-[12px] font-semibold text-orange-700 shadow-sm transition-colors hover:bg-orange-100 active:scale-[0.98]"
  >
    {label}
  </button>
);

const GeoBlock: React.FC<{
  kind: GeoKind;
  coveragePct: number;
  stationName: string;
  verdict: CampaignFeasibilityResponse;
  radiusMiles?: number;
  currentRadiusCenter?: { lat: number; lng: number; label?: string } | null;
  onApplyRadiusCenter?: (lat: number, lon: number, label: string) => void;
  onAddGeoCodes?: (kind: GeoKind, codes: string[]) => void;
  onRemoveGeoTargeting?: () => void;
}> = ({ kind, coveragePct, stationName, verdict, radiusMiles, currentRadiusCenter, onApplyRadiusCenter, onAddGeoCodes, onRemoveGeoTargeting }) => {
  if (kind === 'radius') {
    return (
      <RadiusBlock
        coveragePct={coveragePct}
        stationName={stationName}
        verdict={verdict}
        radiusMiles={radiusMiles}
        currentRadiusCenter={currentRadiusCenter}
        onApplyRadiusCenter={onApplyRadiusCenter}
        onRemoveGeoTargeting={onRemoveGeoTargeting}
      />
    );
  }
  return (
    <RecommendationBlock
      kind={kind}
      coveragePct={coveragePct}
      stationName={stationName}
      verdict={verdict}
      onAddGeoCodes={onAddGeoCodes}
    />
  );
};

const RadiusBlock: React.FC<{
  coveragePct: number;
  stationName: string;
  verdict: CampaignFeasibilityResponse;
  radiusMiles?: number;
  currentRadiusCenter?: { lat: number; lng: number; label?: string } | null;
  onApplyRadiusCenter?: (lat: number, lon: number, label: string) => void;
  onRemoveGeoTargeting?: () => void;
}> = ({
  coveragePct,
  stationName,
  verdict,
  radiusMiles,
  currentRadiusCenter,
  onApplyRadiusCenter,
  onRemoveGeoTargeting,
}) => {
  const center = verdict.stationCenter;
  const atMax = typeof radiusMiles === 'number' && radiusMiles >= RADIUS_MAX_MILES;
  const currentMiles = typeof radiusMiles === 'number' ? Math.round(radiusMiles) : null;

  // CTV-474: when the buyer's pin is already on (or near) the station's
  // home-market center, the recenter copy + button is misleading. Suppress
  // both and shift the copy to the actionable remaining option.
  const alreadyOnHome =
    !!center &&
    !!currentRadiusCenter &&
    Math.abs(currentRadiusCenter.lat - center.lat) < RADIUS_CENTER_TOLERANCE_DEG &&
    Math.abs(currentRadiusCenter.lng - center.lon) < RADIUS_CENTER_TOLERANCE_DEG;

  let copy: string;
  let showCenterButton = false;
  // CTV-474: surface the "Run station-wide" button whenever the only
  // remaining lever is to drop geography targeting altogether.
  let showRemoveButton = false;
  if (center && !alreadyOnHome) {
    copy = atMax
      ? `Your radius covers only ~${coveragePct}% of ${stationName}'s audience. It's at the ${RADIUS_MAX_MILES} mi maximum — try recentering on ${center.label} (${stationName}'s home market).`
      : `Your radius covers only ~${coveragePct}% of ${stationName}'s audience. Center it on ${center.label} (${stationName}'s home market) and expand up to ${RADIUS_MAX_MILES} mi to unlock more inventory.`;
    showCenterButton = !!onApplyRadiusCenter;
  } else if (center && alreadyOnHome) {
    if (atMax) {
      copy = `Your radius covers only ~${coveragePct}% of ${stationName}'s audience even centered on ${center.label} at the ${RADIUS_MAX_MILES} mi maximum. Try removing geography targeting to run station-wide.`;
      showRemoveButton = !!onRemoveGeoTargeting;
    } else {
      copy = `Your radius covers only ~${coveragePct}% of ${stationName}'s audience. Try expanding it up to ${RADIUS_MAX_MILES} mi (currently ${currentMiles ?? '?'} mi) for more inventory.`;
    }
  } else {
    // Station has no center configured.
    if (atMax) {
      copy = `Your radius covers only ~${coveragePct}% of ${stationName}'s audience and is already at the ${RADIUS_MAX_MILES} mi maximum. Try removing geography targeting to run station-wide.`;
      showRemoveButton = !!onRemoveGeoTargeting;
    } else {
      copy = `Your radius covers only ~${coveragePct}% of ${stationName}'s audience. Try widening it (currently ${currentMiles ?? '?'} mi, max ${RADIUS_MAX_MILES} mi).`;
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-orange-200/70">
      <p className="text-[12px] leading-relaxed text-orange-800">{copy}</p>
      {(showCenterButton || showRemoveButton) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {showCenterButton && center && (
            <GeoButton
              label={`Center on ${center.label}`}
              onClick={() => onApplyRadiusCenter!(center.lat, center.lon, center.label)}
            />
          )}
          {showRemoveButton && (
            <GeoButton label="Run station-wide" onClick={() => onRemoveGeoTargeting!()} />
          )}
        </div>
      )}
    </div>
  );
};

const RecommendationBlock: React.FC<{
  kind: GeoKind;
  coveragePct: number;
  stationName: string;
  verdict: CampaignFeasibilityResponse;
  onAddGeoCodes?: (kind: GeoKind, codes: string[]) => void;
}> = ({ kind, coveragePct, stationName, verdict, onAddGeoCodes }) => {
  const recs = verdict.geoRecommendations;
  // Labels for in-copy mentions. Codes for the click handler.
  const codes = recs?.codes ?? [];
  const labels =
    recs?.labels?.length === codes.length && recs.labels.length > 0
      ? recs.labels
      : codes;
  // CTV-474: friendly labels for in-copy mentions.
  //   states: ISO code → full name (Hawaii, not HI)
  //   dmas:   code → "Honolulu (DMA 744)" via the local Nielsen lookup
  //   zips:   ZIP itself
  const friendlyLabels =
    kind === 'states'
      ? labels.map((c) => stateNameForCode(c))
      : kind === 'dmas'
        ? codes.map((c) => `${dmaNameForCode(c)} (DMA ${c})`)
        : labels;
  // Button face labels (no parenthetical for DMAs — keeps the pill compact).
  const buttonLabels =
    kind === 'dmas' ? codes.map((c) => `${dmaNameForCode(c)} (${c})`) : friendlyLabels;

  let copy: string;
  if (codes.length === 0) {
    // Edge case: backend found no unselected geos in the lookback window.
    if (kind === 'zips') {
      copy = `Your ZIPs cover only ~${coveragePct}% of ${stationName}'s audience. Try widening your ZIP selection or switching to DMA / state targeting.`;
    } else if (kind === 'dmas') {
      copy = `Your selected DMAs cover only ~${coveragePct}% of ${stationName}'s audience. Try adding more DMAs.`;
    } else {
      copy = `Your selected states cover only ~${coveragePct}% of ${stationName}'s audience. Try adding more states.`;
    }
  } else if (kind === 'zips') {
    const list = friendlyLabels.slice(0, 3).join(', ');
    copy = `Your ZIPs cover only ~${coveragePct}% of ${stationName}'s audience. ${stationName}'s highest-inventory ZIPs in this area are ${list}.`;
  } else {
    const joined = formatSentenceList(friendlyLabels.slice(0, 3));
    const noun = kind === 'dmas' ? 'DMAs' : 'states';
    copy = `Your selected ${noun} cover only ~${coveragePct}% of ${stationName}'s audience. ${stationName} also has strong inventory in ${joined}.`;
  }

  return (
    <div className="mt-3 pt-3 border-t border-orange-200/70">
      <p className="text-[12px] leading-relaxed text-orange-800">{copy}</p>
      {codes.length > 0 && onAddGeoCodes && (
        <div className="mt-2 flex flex-wrap gap-2">
          {kind === 'zips' ? (
            <GeoButton
              label="Add suggested ZIPs"
              onClick={() => onAddGeoCodes(kind, codes.slice(0, 3))}
            />
          ) : (
            codes
              .slice(0, 3)
              .map((code, i) => (
                <GeoButton
                  key={code}
                  label={`Add ${buttonLabels[i] ?? code}`}
                  onClick={() => onAddGeoCodes(kind, [code])}
                />
              ))
          )}
        </div>
      )}
    </div>
  );
};

function formatSentenceList(labels: string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

export default InventoryWarningCard;
